// ============================================================================
// DRAWING VERSION COMPARE - Side-by-side and overlay version comparison
// Date: January 2026
// ============================================================================

'use client';

/* eslint-disable react-hooks/set-state-in-effect, react-hooks/exhaustive-deps -- compare/reset UI state when versions or sync toggles change */
import React, { useState, useRef, useEffect } from 'react';
import {
  X, ZoomIn, ZoomOut, RotateCw, Layers, Eye, EyeOff,
  ArrowLeftRight, GitCompare, ChevronLeft, ChevronRight,
  Download, Calendar, User, Tag, FileText, Minus, Plus,
  Check, AlertTriangle
} from 'lucide-react';
import type { Drawing, DrawingVersion } from './types';
import { STATUS_CONFIG, FILE_TYPE_CONFIG } from './types';
import { resolveDrawingFileUrl } from './drawingFileUrl';

// ============================================================================
// TYPES
// ============================================================================

interface DrawingVersionCompareProps {
  drawing: Drawing;
  versions: DrawingVersion[];
  isOpen: boolean;
  onClose: () => void;
  onRestoreVersion?: (version: DrawingVersion) => void;
}

type CompareMode = 'side-by-side' | 'overlay' | 'slider';

// ============================================================================
// CONSTANTS
// ============================================================================

const formatDate = (date: string) => {
  return new Date(date).toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
};

const canPreviewAsImage = (fileType: string): boolean => {
  const type = fileType?.toLowerCase() || '';
  return ['png', 'jpg', 'jpeg', 'webp', 'gif', 'bmp', 'svg'].includes(type);
};

// ============================================================================
// VERSION CARD COMPONENT
// ============================================================================

interface VersionCardProps {
  version: DrawingVersion;
  isSelected: boolean;
  onSelect: () => void;
  position: 'left' | 'right';
  isCurrent?: boolean;
}

function VersionCard({ version, isSelected, onSelect, position, isCurrent }: VersionCardProps) {
  return (
    <button
      onClick={onSelect}
      className={`w-full text-left p-3 rounded-lg border transition-all ${
        isSelected
          ? position === 'left'
            ? 'border-blue-500 bg-blue-500/10'
            : 'border-green-500 bg-green-500/10'
          : 'border-gray-700 bg-gray-800/50 hover:border-gray-600'
      }`}
    >
      <div className="flex items-center justify-between mb-2">
        <span className={`font-semibold ${position === 'left' ? 'text-blue-400' : 'text-green-400'}`}>
          Rev {version.revision}
        </span>
        <div className="flex items-center gap-2">
          {isCurrent && (
            <span className="text-xs px-2 py-0.5 bg-amber-500/20 text-amber-400 rounded">Current</span>
          )}
          <span className="text-xs text-gray-500">v{version.version}</span>
        </div>
      </div>
      
      <div className="space-y-1 text-xs text-gray-400">
        <div className="flex items-center gap-2">
          <Calendar className="w-3.5 h-3.5" />
          {formatDate(version.created_at)}
        </div>
        <div className="flex items-center gap-2">
          <User className="w-3.5 h-3.5" />
          {version.uploaded_by_name || 'Unknown'}
        </div>
        {version.change_description && (
          <div className="flex items-start gap-2">
            <FileText className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
            <span className="line-clamp-2">{version.change_description}</span>
          </div>
        )}
      </div>
    </button>
  );
}

// ============================================================================
// SIDE BY SIDE VIEW COMPONENT
// ============================================================================

interface SideBySideViewProps {
  leftVersion: DrawingVersion | null;
  rightVersion: DrawingVersion | null;
  zoom: number;
  rotation: number;
  syncZoom: boolean;
  syncRotation: boolean;
}

function SideBySideView({ leftVersion, rightVersion, zoom, rotation, syncZoom, syncRotation }: SideBySideViewProps) {
  const [leftZoom, setLeftZoom] = useState(zoom);
  const [rightZoom, setRightZoom] = useState(zoom);
  const [leftRotation, setLeftRotation] = useState(rotation);
  const [rightRotation, setRightRotation] = useState(rotation);

  useEffect(() => {
    if (syncZoom) {
      setLeftZoom(zoom);
      setRightZoom(zoom);
    }
  }, [zoom, syncZoom]);

  useEffect(() => {
    if (syncRotation) {
      setLeftRotation(rotation);
      setRightRotation(rotation);
    }
  }, [rotation, syncRotation]);

  const renderImage = (version: DrawingVersion | null, imageZoom: number, imageRotation: number, side: 'left' | 'right') => {
    if (!version) {
      return (
        <div className="flex-1 flex items-center justify-center bg-gray-900 border border-gray-700 rounded-lg">
          <p className="text-gray-500">Select a version</p>
        </div>
      );
    }

    const fileType = version.file_type?.toLowerCase() || '';
    const canPreview = canPreviewAsImage(fileType);

    return (
      <div className="flex-1 relative overflow-hidden bg-gray-900 border border-gray-700 rounded-lg">
        {/* Header */}
        <div className={`absolute top-0 left-0 right-0 z-10 p-2 bg-gradient-to-b ${side === 'left' ? 'from-blue-900/80' : 'from-green-900/80'} to-transparent`}>
          <div className="flex items-center justify-between">
            <span className={`text-sm font-medium ${side === 'left' ? 'text-blue-300' : 'text-green-300'}`}>
              Rev {version.revision} (v{version.version})
            </span>
            <span className="text-xs text-gray-400">{formatDate(version.created_at)}</span>
          </div>
        </div>

        {/* Image */}
        {canPreview ? (
          <div className="w-full h-full flex items-center justify-center p-8">
            <img
              src={resolveDrawingFileUrl(version.file_url)}
              alt={`Version ${version.version}`}
              className="max-w-full max-h-full object-contain transition-transform"
              style={{
                transform: `scale(${imageZoom}) rotate(${imageRotation}deg)`
              }}
            />
          </div>
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <div className="text-center">
              <FileText className="w-16 h-16 text-gray-600 mx-auto mb-2" />
              <p className="text-gray-400">Preview not available</p>
              <p className="text-xs text-gray-500 mt-1">{fileType.toUpperCase()} file</p>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="flex-1 flex gap-4 p-4">
      {renderImage(leftVersion, syncZoom ? zoom : leftZoom, syncRotation ? rotation : leftRotation, 'left')}
      {renderImage(rightVersion, syncZoom ? zoom : rightZoom, syncRotation ? rotation : rightRotation, 'right')}
    </div>
  );
}

// ============================================================================
// OVERLAY VIEW COMPONENT
// ============================================================================

interface OverlayViewProps {
  leftVersion: DrawingVersion | null;
  rightVersion: DrawingVersion | null;
  zoom: number;
  rotation: number;
  opacity: number;
}

function OverlayView({ leftVersion, rightVersion, zoom, rotation, opacity }: OverlayViewProps) {
  if (!leftVersion || !rightVersion) {
    return (
      <div className="flex-1 flex items-center justify-center bg-gray-900">
        <p className="text-gray-500">Select two versions to compare</p>
      </div>
    );
  }

  const canPreview = canPreviewAsImage(leftVersion.file_type || '') && canPreviewAsImage(rightVersion.file_type || '');

  if (!canPreview) {
    return (
      <div className="flex-1 flex items-center justify-center bg-gray-900">
        <p className="text-gray-500">Overlay compare only available for image files</p>
      </div>
    );
  }

  return (
    <div className="flex-1 relative overflow-hidden bg-gray-900">
      {/* Left image (base) */}
      <div className="absolute inset-0 flex items-center justify-center p-8">
        <img
          src={resolveDrawingFileUrl(leftVersion.file_url)}
          alt={`Version ${leftVersion.version}`}
          className="max-w-full max-h-full object-contain"
          style={{
            transform: `scale(${zoom}) rotate(${rotation}deg)`,
            filter: 'grayscale(100%)'
          }}
        />
      </div>

      {/* Right image (overlay) */}
      <div 
        className="absolute inset-0 flex items-center justify-center p-8"
        style={{ opacity }}
      >
        <img
          src={resolveDrawingFileUrl(rightVersion.file_url)}
          alt={`Version ${rightVersion.version}`}
          className="max-w-full max-h-full object-contain"
          style={{
            transform: `scale(${zoom}) rotate(${rotation}deg)`,
            mixBlendMode: 'difference'
          }}
        />
      </div>

      {/* Legend */}
      <div className="absolute bottom-4 left-4 bg-gray-900/90 backdrop-blur rounded-lg p-3 text-sm">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-gray-500 rounded" />
            <span className="text-gray-400">Rev {leftVersion.revision} (base)</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-white rounded" style={{ opacity }} />
            <span className="text-gray-400">Rev {rightVersion.revision} (changes)</span>
          </div>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// SLIDER VIEW COMPONENT
// ============================================================================

interface SliderViewProps {
  leftVersion: DrawingVersion | null;
  rightVersion: DrawingVersion | null;
  zoom: number;
  rotation: number;
}

function SliderView({ leftVersion, rightVersion, zoom, rotation }: SliderViewProps) {
  const [sliderPosition, setSliderPosition] = useState(50);
  const containerRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging || !containerRef.current) return;
    
    const rect = containerRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const percentage = Math.max(0, Math.min(100, (x / rect.width) * 100));
    setSliderPosition(percentage);
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  if (!leftVersion || !rightVersion) {
    return (
      <div className="flex-1 flex items-center justify-center bg-gray-900">
        <p className="text-gray-500">Select two versions to compare</p>
      </div>
    );
  }

  const canPreview = canPreviewAsImage(leftVersion.file_type || '') && canPreviewAsImage(rightVersion.file_type || '');

  if (!canPreview) {
    return (
      <div className="flex-1 flex items-center justify-center bg-gray-900">
        <p className="text-gray-500">Slider compare only available for image files</p>
      </div>
    );
  }

  return (
    <div 
      ref={containerRef}
      className="flex-1 relative overflow-hidden bg-gray-900 cursor-col-resize"
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseUp}
    >
      {/* Right image (full width) */}
      <div className="absolute inset-0 flex items-center justify-center p-8">
        <img
          src={resolveDrawingFileUrl(rightVersion.file_url)}
          alt={`Version ${rightVersion.version}`}
          className="max-w-full max-h-full object-contain"
          style={{ transform: `scale(${zoom}) rotate(${rotation}deg)` }}
        />
      </div>

      {/* Left image (clipped) */}
      <div 
        className="absolute inset-0 flex items-center justify-center p-8 overflow-hidden"
        style={{ clipPath: `inset(0 ${100 - sliderPosition}% 0 0)` }}
      >
        <img
          src={resolveDrawingFileUrl(leftVersion.file_url)}
          alt={`Version ${leftVersion.version}`}
          className="max-w-full max-h-full object-contain"
          style={{ transform: `scale(${zoom}) rotate(${rotation}deg)` }}
        />
      </div>

      {/* Slider handle */}
      <div 
        className="absolute top-0 bottom-0 w-1 bg-white/80 cursor-col-resize z-10"
        style={{ left: `${sliderPosition}%` }}
        onMouseDown={() => setIsDragging(true)}
      >
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-white shadow-lg flex items-center justify-center">
          <ArrowLeftRight className="w-4 h-4 text-gray-700" />
        </div>
      </div>

      {/* Labels */}
      <div className="absolute top-4 left-4 bg-blue-600 text-white text-xs px-2 py-1 rounded">
        Rev {leftVersion.revision}
      </div>
      <div className="absolute top-4 right-4 bg-green-600 text-white text-xs px-2 py-1 rounded">
        Rev {rightVersion.revision}
      </div>
    </div>
  );
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export default function DrawingVersionCompare({
  drawing,
  versions,
  isOpen,
  onClose,
  onRestoreVersion
}: DrawingVersionCompareProps) {
  // State
  const [compareMode, setCompareMode] = useState<CompareMode>('side-by-side');
  const [leftVersion, setLeftVersion] = useState<DrawingVersion | null>(null);
  const [rightVersion, setRightVersion] = useState<DrawingVersion | null>(null);
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [opacity, setOpacity] = useState(0.5);
  const [syncZoom, setSyncZoom] = useState(true);
  const [syncRotation, setSyncRotation] = useState(true);
  const [showVersionList, setShowVersionList] = useState(true);

  // Sort versions by date (newest first)
  const sortedVersions = [...versions].sort(
    (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
  );

  // Auto-select latest two versions on open
  useEffect(() => {
    if (isOpen && sortedVersions.length >= 2 && !leftVersion && !rightVersion) {
      setRightVersion(sortedVersions[0]); // Newest
      setLeftVersion(sortedVersions[1]); // Previous
    } else if (isOpen && sortedVersions.length === 1 && !rightVersion) {
      setRightVersion(sortedVersions[0]);
    }
  }, [isOpen, sortedVersions]);

  // Reset on close
  useEffect(() => {
    if (!isOpen) {
      setLeftVersion(null);
      setRightVersion(null);
      setZoom(1);
      setRotation(0);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black z-[200] flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between p-3 bg-gray-900 border-b border-gray-800">
        <div className="flex items-center gap-4">
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-gray-800 text-gray-400"
          >
            <X className="w-5 h-5" />
          </button>
          
          <div>
            <h2 className="font-semibold text-white flex items-center gap-2">
              <GitCompare className="w-5 h-5 text-blue-400" />
              Compare Versions
            </h2>
            <p className="text-sm text-gray-400">{drawing.drawing_name}</p>
          </div>
        </div>

        {/* Mode selector */}
        <div className="flex items-center gap-2">
          <div className="flex rounded-lg bg-gray-800 p-1">
            <button
              onClick={() => setCompareMode('side-by-side')}
              className={`px-3 py-1.5 rounded text-sm flex items-center gap-2 ${
                compareMode === 'side-by-side' ? 'bg-blue-600 text-white' : 'text-gray-400 hover:text-white'
              }`}
            >
              <Layers className="w-4 h-4" />
              Side by Side
            </button>
            <button
              onClick={() => setCompareMode('overlay')}
              className={`px-3 py-1.5 rounded text-sm flex items-center gap-2 ${
                compareMode === 'overlay' ? 'bg-blue-600 text-white' : 'text-gray-400 hover:text-white'
              }`}
            >
              <Eye className="w-4 h-4" />
              Overlay
            </button>
            <button
              onClick={() => setCompareMode('slider')}
              className={`px-3 py-1.5 rounded text-sm flex items-center gap-2 ${
                compareMode === 'slider' ? 'bg-blue-600 text-white' : 'text-gray-400 hover:text-white'
              }`}
            >
              <ArrowLeftRight className="w-4 h-4" />
              Slider
            </button>
          </div>
        </div>

        {/* View controls */}
        <div className="flex items-center gap-2">
          {/* Zoom */}
          <button
            onClick={() => setZoom(z => Math.max(z - 0.25, 0.25))}
            className="p-2 rounded hover:bg-gray-800 text-gray-400"
          >
            <ZoomOut className="w-5 h-5" />
          </button>
          <span className="text-sm text-white min-w-[60px] text-center">{Math.round(zoom * 100)}%</span>
          <button
            onClick={() => setZoom(z => Math.min(z + 0.25, 5))}
            className="p-2 rounded hover:bg-gray-800 text-gray-400"
          >
            <ZoomIn className="w-5 h-5" />
          </button>

          <div className="w-px h-6 bg-gray-700 mx-1" />

          {/* Rotate */}
          <button
            onClick={() => setRotation(r => (r + 90) % 360)}
            className="p-2 rounded hover:bg-gray-800 text-gray-400"
          >
            <RotateCw className="w-5 h-5" />
          </button>

          <div className="w-px h-6 bg-gray-700 mx-1" />

          {/* Version list toggle */}
          <button
            onClick={() => setShowVersionList(!showVersionList)}
            className={`p-2 rounded ${showVersionList ? 'bg-blue-600 text-white' : 'hover:bg-gray-800 text-gray-400'}`}
          >
            <FileText className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Main content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Version list sidebar */}
        {showVersionList && (
          <div className="w-72 bg-gray-900 border-r border-gray-800 flex flex-col">
            <div className="p-3 border-b border-gray-800">
              <h3 className="text-sm font-semibold text-white">Version History</h3>
              <p className="text-xs text-gray-500 mt-1">{sortedVersions.length} versions</p>
            </div>

            <div className="flex-1 overflow-y-auto p-3 space-y-4">
              {/* Left version selector */}
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-3 h-3 rounded-full bg-blue-500" />
                  <span className="text-xs font-medium text-blue-400">Base Version</span>
                </div>
                <div className="space-y-2">
                  {sortedVersions.map((v, idx) => (
                    <VersionCard
                      key={v.id}
                      version={v}
                      isSelected={leftVersion?.id === v.id}
                      onSelect={() => setLeftVersion(v)}
                      position="left"
                      isCurrent={idx === 0}
                    />
                  ))}
                </div>
              </div>

              {/* Right version selector */}
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-3 h-3 rounded-full bg-green-500" />
                  <span className="text-xs font-medium text-green-400">Compare To</span>
                </div>
                <div className="space-y-2">
                  {sortedVersions.map((v, idx) => (
                    <VersionCard
                      key={v.id}
                      version={v}
                      isSelected={rightVersion?.id === v.id}
                      onSelect={() => setRightVersion(v)}
                      position="right"
                      isCurrent={idx === 0}
                    />
                  ))}
                </div>
              </div>
            </div>

            {/* Restore button */}
            {onRestoreVersion && leftVersion && leftVersion.id !== sortedVersions[0]?.id && (
              <div className="p-3 border-t border-gray-800">
                <button
                  onClick={() => onRestoreVersion(leftVersion)}
                  className="w-full py-2 bg-amber-600 hover:bg-amber-700 text-white rounded-lg text-sm flex items-center justify-center gap-2"
                >
                  <RotateCw className="w-4 h-4" />
                  Restore Rev {leftVersion.revision}
                </button>
              </div>
            )}
          </div>
        )}

        {/* Comparison view */}
        <div className="flex-1 flex flex-col">
          {/* Overlay controls */}
          {compareMode === 'overlay' && (
            <div className="flex items-center justify-center gap-4 p-2 bg-gray-900 border-b border-gray-800">
              <span className="text-sm text-gray-400">Overlay Opacity:</span>
              <input
                type="range"
                min="0"
                max="100"
                value={opacity * 100}
                onChange={(e) => setOpacity(parseInt(e.target.value) / 100)}
                className="w-48"
              />
              <span className="text-sm text-white min-w-[40px]">{Math.round(opacity * 100)}%</span>
            </div>
          )}

          {/* Sync controls for side-by-side */}
          {compareMode === 'side-by-side' && (
            <div className="flex items-center justify-center gap-6 p-2 bg-gray-900 border-b border-gray-800">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={syncZoom}
                  onChange={(e) => setSyncZoom(e.target.checked)}
                  className="rounded"
                />
                <span className="text-sm text-gray-400">Sync Zoom</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={syncRotation}
                  onChange={(e) => setSyncRotation(e.target.checked)}
                  className="rounded"
                />
                <span className="text-sm text-gray-400">Sync Rotation</span>
              </label>
            </div>
          )}

          {/* View area */}
          {compareMode === 'side-by-side' && (
            <SideBySideView
              leftVersion={leftVersion}
              rightVersion={rightVersion}
              zoom={zoom}
              rotation={rotation}
              syncZoom={syncZoom}
              syncRotation={syncRotation}
            />
          )}

          {compareMode === 'overlay' && (
            <OverlayView
              leftVersion={leftVersion}
              rightVersion={rightVersion}
              zoom={zoom}
              rotation={rotation}
              opacity={opacity}
            />
          )}

          {compareMode === 'slider' && (
            <SliderView
              leftVersion={leftVersion}
              rightVersion={rightVersion}
              zoom={zoom}
              rotation={rotation}
            />
          )}
        </div>
      </div>

      {/* Footer with version details */}
      {(leftVersion || rightVersion) && (
        <div className="flex items-center justify-between p-3 bg-gray-900 border-t border-gray-800 text-sm">
          <div className="flex items-center gap-6">
            {leftVersion && (
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-blue-500" />
                <span className="text-gray-400">
                  Rev {leftVersion.revision}: {leftVersion.change_description || 'No description'}
                </span>
              </div>
            )}
          </div>
          <div className="flex items-center gap-6">
            {rightVersion && (
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-green-500" />
                <span className="text-gray-400">
                  Rev {rightVersion.revision}: {rightVersion.change_description || 'No description'}
                </span>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
